// Last-resort reload circuit breaker for "Organization id does not match the
// current context" 409s from the API (see api/index.ts _handleResponse).
// Capped at 3 reloads in 30s so a misbehaving server can't pin the browser
// in a reload loop.

import { logError } from "../helpers/logError";

const CIRCUIT_BREAKER_STORAGE_KEY = "contact:orgSyncReloadTimestamps";
const MAX_RELOADS = 3;
const CIRCUIT_BREAKER_WINDOW_MS = 30_000;

let reloadPending = false;

function getReloadTimestamps(): number[] {
	try {
		const raw = sessionStorage.getItem(CIRCUIT_BREAKER_STORAGE_KEY);
		return raw ? JSON.parse(raw) : [];
	} catch {
		return [];
	}
}

function setReloadTimestamps(timestamps: number[]): void {
	try {
		sessionStorage.setItem(CIRCUIT_BREAKER_STORAGE_KEY, JSON.stringify(timestamps));
	} catch {
		// sessionStorage may be unavailable in some contexts
	}
}

export function canReloadForOrgSwitch(): boolean {
	if (reloadPending) {
		return false;
	}
	const now = Date.now();
	const reloadTimestamps = getReloadTimestamps().filter((timestamp) => now - timestamp < CIRCUIT_BREAKER_WINDOW_MS);
	if (reloadTimestamps.length >= MAX_RELOADS) {
		// User is wedged on a stale-org page that can't be recovered without
		// human intervention. Surface it so on-call sees the rate.
		logError("useOrgSync: org-mismatch reload breaker tripped", new Error("circuit breaker open"), {
			reloadCount: reloadTimestamps.length,
			windowMs: CIRCUIT_BREAKER_WINDOW_MS
		});
		return false;
	}
	logError("useOrgSync: forcing reload due to org-context mismatch", new Error("org-context mismatch"), {
		reloadCount: reloadTimestamps.length + 1
	});
	reloadTimestamps.push(now);
	setReloadTimestamps(reloadTimestamps);
	reloadPending = true;
	return true;
}

export function isReloadPending(): boolean {
	return reloadPending;
}

export function _resetOrgSync(): void {
	reloadPending = false;
	try {
		sessionStorage.removeItem(CIRCUIT_BREAKER_STORAGE_KEY);
	} catch {
		// sessionStorage may be unavailable in some contexts
	}
}
